import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-whole-genome"),
  kind: "test",
  tags: ["testing", "genetic-screening", "lab"],
  title: "Whole Genome Sequencing",
  subtitle:
    "Broader than carrier screening: carrier status, diagnostic variants, and medication metabolism",
  icon: "🧬",
  body: {
    testName: "Whole Genome Sequencing (WGS)",
    category: "genetic",
    whatItMeasures:
      "A broad readout of your DNA that can be used for several kinds of reports: reproductive carrier status, single-gene findings, hereditary risk variants such as BRCA1 or BRCA2, medication metabolism, and polygenic risk estimates for some common conditions.\n\nThe point is to create one reusable genetic record that can support fertility planning now and be re-analyzed as science improves.\n\nIt does not predict everything or diagnose disease on its own.",
    howItWorks:
      "A blood or saliva sample is collected, depending on the test kit and lab workflow. DNA is extracted, sequenced, analyzed for different classes of genetic findings, and turned into a report that can be reviewed with fertility, family-building, and medication decisions in mind.",
    quickFacts: [
      { kind: "sample", value: "Blood or saliva" },
      { kind: "time", value: "5 min draw or saliva kit" },
      { kind: "pain", value: "Needle pinch if blood" },
      { kind: "prep", value: "Genetic consent and report preferences" },
      { kind: "results", value: "2-3 weeks" },
    ],
    howItWorksSteps: [
      "You provide a blood or saliva sample. Blood may be collected at a lab or, when available, through mobile phlebotomy at home or work; saliva can usually be collected with a kit.",
      "The lab extracts DNA from the sample and runs it through a sequencing machine that reads the DNA letters across the genome.",
      "Bioinformatics software aligns those reads to a reference genome and looks for variants across genes, regulatory regions, and other clinically relevant parts of the genome.",
      "The analysis separates findings into categories such as carrier status, monogenic or diagnostic variants, hereditary risk variants like BRCA1 or BRCA2, pharmacogenomic markers, and polygenic risk information when appropriate.",
      "Fertility-specific analysis can include carrier findings, rare monogenic variants, pharmacogenomics for medications such as letrozole, gonadotropins, or progesterone, and risk context for conditions such as PMOS (ex PCOS), endometriosis, premature ovarian insufficiency, or recurrent loss.",
      "Those findings are interpreted into a report that highlights reproductive genetics, health risks that may matter for planning, medication metabolism, and items worth reviewing with a genetic counselor or clinician.",
      "When a raw data file is available, it can be kept with the report so the information can be re-analyzed over time or connected to the Louise assistant for future questions.",
    ],
    whyItMatters: [
      "It can return the same carrier-status findings as a focused expanded carrier panel (though professional bodies like ACOG currently endorse expanded carrier panels - not WGS - for routine reproductive screening).",
      "It creates one reusable genetic record that can support future fertility care, pregnancy planning, and medication decisions.",
      "It may surface clinically relevant variants that smaller panels would not evaluate.",
      "It can help your care team personalize decisions around embryo testing, donor options, medication metabolism, and genetic counseling.",
      "The DNA result itself does not change over time, but interpretation can improve as databases, science, and reporting tools improve.",
      "A whole genome can support multiple report layers from the same sample instead of ordering a separate small panel every time a new question comes up.",
    ],
    expectations: {
      canExpect: [
        "Carrier screening information to be generated from the broader genome analysis when the lab offers that report layer",
        "A reusable genetic record that can support fertility, pregnancy, medication, and future health questions",
        "Pharmacogenomic and risk-report layers when those are included in the ordered product",
      ],
      shouldNotExpect: [
        "Every lab to return the same report categories from the same raw sequencing data",
        "Polygenic risk scores or variants of uncertain significance to be interpreted like a single definitive diagnosis",
        "The result to replace genetic counseling when findings are complex or reproductive decisions depend on the result",
      ],
    },
    considerations: [
      "Ask which report layers are included: carrier status, pharmacogenomics, monogenic findings, hereditary risk variants, polygenic risk, and raw data access are not always bundled the same way.",
      "If you are choosing between expanded carrier screening and whole genome sequencing, whole genome is the broader test; the carrier report is one possible output from it.",
      "For couples, both partners need compatible reporting if the goal is reproductive risk assessment.",
    ],
    turnaroundTime: "2-3 weeks",
    atHome: false,
    clinicRequired: false,
    dataPoints: [],
    section: "genetic-screening",
  },
  sources: [
    {
      title:
        "ACMG SF v3.0 list for reporting of secondary findings in clinical exome and genome sequencing",
      authors: "Miller DT et al.",
      journal: "Genetics in Medicine",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/34012068/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Carrier Screening in the Age of Genomic Medicine (Committee Opinion No. 690)",
      authors: "American College of Obstetricians and Gynecologists",
      journal: "Obstetrics & Gynecology",
      year: 2017,
      url: "https://pubmed.ncbi.nlm.nih.gov/28225425/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "Exome and genome sequencing for pediatric patients with congenital anomalies or intellectual disability: an evidence-based clinical guideline of the ACMG",
      authors: "Manickam K et al.",
      journal: "Genetics in Medicine",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/34211152/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 5,
};

registerBlock(test);
